import { APP_INITIALIZER, ModuleWithProviders, NgModule } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FEATURE_CONFIG, FeatureConfig } from './feature-toggles.config';
import { TogglePointByParamGuard } from './toggle-point/toggle-point-by-param.guard';
import { initialize } from './toggle-point/toggle-point-function-helper';
import { TogglePointDirective } from './toggle-point/toggle-point.directive';
import { TogglePointGuard } from './toggle-point/toggle-point.guard';
import { FEATURE_TOGGLER } from './feature-toggler';
import { LocalFeatureTogglerRepository, LocalFeatureTogglerRepositoryFactory } from './local-feature-toggler.service';
import { RemoteFeatureTogglerServiceFactory } from './remote-feature-toggler-factory';
import { FEATURE_API_CONFIG, FeatureApiConfig } from './api/feature-flags-api.config';
import { FeatureFlagContextService } from './client/feature-flag-context.service';
import { FeatureFlagClientService } from './client/feature-flag-client.service';
import { FeatureFlagsApiService } from './api/feature-flags-api.service';
import { HybridFeatureTogglerServiceFactory } from './hybrid-feature-toggler-factory';
import { RemoteFeatureTogglerService } from './remote-feature-toggler.service';
import { CbreLoggerToken } from '@cbre/logger';

// Import admin module
import { FeatureFlagsAdminModule } from './admin/feature-flags-admin.module';

/**
 * Configuration options for the FeatureTogglesModule
 */
export interface FeatureTogglesConfig {
  /**
   * Local feature configuration
   */
  featureConfig?: FeatureConfig;

  /**
   * API configuration for remote feature flags
   */
  apiConfig?: FeatureApiConfig;
}

const providers = [
  { provide: APP_INITIALIZER, deps: [FEATURE_TOGGLER], useFactory: initialize, multi: true },
  TogglePointGuard,
  TogglePointByParamGuard,
];

@NgModule({
  declarations: [TogglePointDirective],
  exports: [TogglePointDirective, FeatureFlagsAdminModule],
  imports: [FeatureFlagsAdminModule],
  providers: [
    LocalFeatureTogglerRepository,
    FeatureFlagContextService,
    FeatureFlagClientService,
    FeatureFlagsApiService,
    ...providers,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class FeatureTogglesModule {
  /**
   * Import the module using this static method to import the providers.
   * This must be imported on the core module of the project.
   *
   * @param options Module configuration options
   */
  static forRoot(options: FeatureTogglesConfig = {}): ModuleWithProviders<FeatureTogglesModule> {
    return {
      ngModule: FeatureTogglesModule,
      providers: [
        { provide: FEATURE_CONFIG, useValue: options.featureConfig },
        { provide: FEATURE_TOGGLER, useFactory: LocalFeatureTogglerRepositoryFactory, deps: [FEATURE_CONFIG] },
        ...providers,
      ],
    };
  }

  /**
   * Import the module using this static method to enable remote feature toggling.
   * This will fetch feature flags from a remote API endpoint and evaluate them client-side.
   *
   * @param options Module configuration options
   */
  static forRemote(options: FeatureTogglesConfig): ModuleWithProviders<FeatureTogglesModule> {
    if (!options.apiConfig) {
      throw new Error('apiConfig is required for remote feature toggling');
    }

    return {
      ngModule: FeatureTogglesModule,
      providers: [
        { provide: FEATURE_API_CONFIG, useValue: options.apiConfig },
        FeatureFlagContextService,
        FeatureFlagClientService,
        FeatureFlagsApiService,
        {
          provide: FEATURE_TOGGLER,
          useFactory: RemoteFeatureTogglerServiceFactory,
          deps: [HttpClient, FEATURE_API_CONFIG, FeatureFlagContextService, FeatureFlagClientService, CbreLoggerToken],
        },
        ...providers,
      ],
    };
  }

  /**
   * Import the module using this static method when you want to use both local and remote feature toggling.
   * Local configurations will override remote flags when both are present.
   * Remote flags are fetched from the API but evaluated client-side for better performance.
   *
   * @param options Module configuration options
   */
  static forHybrid(options: FeatureTogglesConfig): ModuleWithProviders<FeatureTogglesModule> {
    if (!options.featureConfig) {
      throw new Error('featureConfig is required for hybrid feature toggling');
    }
    if (!options.apiConfig) {
      throw new Error('apiConfig is required for hybrid feature toggling');
    }

    return {
      ngModule: FeatureTogglesModule,
      providers: [
        { provide: FEATURE_CONFIG, useValue: options.featureConfig },
        { provide: FEATURE_API_CONFIG, useValue: options.apiConfig },
        {
          provide: RemoteFeatureTogglerService,
          useFactory: RemoteFeatureTogglerServiceFactory,
          deps: [HttpClient, FEATURE_API_CONFIG, FeatureFlagContextService, FeatureFlagClientService, CbreLoggerToken],
        },
        {
          provide: FEATURE_TOGGLER,
          useFactory: HybridFeatureTogglerServiceFactory,
          deps: [FEATURE_CONFIG, RemoteFeatureTogglerService],
        },
        ...providers,
      ],
    };
  }

  /**
   * Import the module using this static method in feature modules.
   * This method only imports the module without providing any feature toggling service.
   * Use this in any feature module to ensure the toggle directives work without
   * overriding the feature toggler configuration.
   */
  static forChild(): ModuleWithProviders<FeatureTogglesModule> {
    return {
      ngModule: FeatureTogglesModule,
      providers: [], // No providers, to avoid overriding the root configuration
    };
  }
}
